import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject, takeUntil, debounceTime, distinctUntilChanged } from 'rxjs';
import { ConfigurationManagerService } from '../../services/ConfigurationManagerService';
import { AutoSaveDemoService, AutoSaveStatus } from '../../services/AutoSaveDemoService';
import { HistoryManagerService, HistoryState, HistoryEntry } from '../../services/HistoryManagerService';

/**
 * Composant de démonstration des fonctionnalités de sauvegarde automatique et historique
 * Gère des formulaires multilingues avec undo/redo et sauvegarde automatique sur changement
 */
@Component({
  selector: 'app-form-demo',
  templateUrl: './form-demo.component.html',
  styleUrls: ['./form-demo.component.scss'],
  standalone: false
})
export class FormDemoComponent implements OnInit, OnDestroy {
  /** Subject pour la gestion de la destruction du composant */
  private destroy$ = new Subject<void>();
  
  /** Formulaire réactif principal */
  demoForm: FormGroup;
  
  /** Langue actuellement sélectionnée */
  currentLanguage = 'fr';
  
  /** Liste des langues disponibles */
  availableLanguages: string[] = [];
  
  /** Langue principale pour la synchronisation */
  primaryLanguage = 'fr';

  /** État actuel du service de sauvegarde automatique */
  autoSaveStatus: AutoSaveStatus = {
    isActive: false,
    lastSaveTime: null,
    saveInProgress: false,
    saveError: null,
    debounceDelay: 1000,
    totalSaves: 0
  };
  
  /** État actuel de l'historique des modifications */
  historyState: HistoryState = {
    entries: [],
    currentIndex: -1,
    maxHistorySize: 50,
    canUndo: false,
    canRedo: false
  };

  /** Liste des entrées récentes d'historique pour affichage */
  recentHistory: HistoryEntry[] = [];
  
  /** Options disponibles pour le champ catégorie */
  categoryOptions = [
    { value: 'event', label: 'Événement' },
    { value: 'product', label: 'Produit' },
    { value: 'service', label: 'Service' },
    { value: 'news', label: 'Actualité' }
  ];

  /** Options disponibles pour le champ thème */
  themeOptions = [
    { value: 'light', label: 'Clair' },
    { value: 'dark', label: 'Sombre' },
    { value: 'auto', label: 'Automatique' }
  ];

  /**
   * Constructeur du composant
   * @param fb FormBuilder pour la création de formulaires réactifs
   * @param configManager Service de gestion des configurations multilingues
   * @param autoSaveService Service de sauvegarde automatique
   * @param historyManager Service de gestion de l'historique
   */
  constructor(
    private fb: FormBuilder,
    private configManager: ConfigurationManagerService,
    public autoSaveService: AutoSaveDemoService,
    private historyManager: HistoryManagerService
  ) {
    this.createForm();
  }

  /**
   * Initialisation du composant
   */
  ngOnInit(): void {
    this.setupSubscriptions();
    this.initializeForm().catch(error => {
      console.error('❌ Erreur lors de l\'initialisation du formulaire:', error);
    });
  }

  /**
   * Nettoyage lors de la destruction du composant
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.autoSaveService.stopAutoSave();
  }

  /**
   * Gestionnaire des raccourcis clavier globaux pour undo/redo
   * @param event Événement clavier
   */
  @HostListener('window:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    if ((event.ctrlKey || event.metaKey)) {
      if (event.key === 'z' && !event.shiftKey) {
        event.preventDefault();
        this.undo();
      } else if ((event.key === 'y') || (event.key === 'z' && event.shiftKey)) {
        event.preventDefault();
        this.redo();
      }
    }
  }

  /**
   * Création du formulaire réactif avec validateurs
   */
  private createForm(): void {
    this.demoForm = this.fb.group({
      title: ['', [Validators.required, Validators.minLength(3)]],
      description: ['', [Validators.required, Validators.minLength(10)]],
      category: ['', Validators.required],
      tags: [[]],
      content: this.fb.group({
        welcomeMessage: [''],
        instructions: [''],
        footerText: ['']
      }),
      settings: this.fb.group({
        enableNotifications: [true],
        autoSave: [true],
        theme: ['light']
      })
    });
  }

  /**
   * Configuration des souscriptions aux observables des services
   */
  private setupSubscriptions(): void {
    // Écouter les changements de configuration
    this.configManager.currentConfigurationObservable
      .pipe(takeUntil(this.destroy$))
      .subscribe(config => {
        if (config) {
          this.loadFormData(config);
        }
      });

    // Écouter les changements du formulaire
    this.demoForm.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(500),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
      )
      .subscribe(formValue => {
        this.updateConfiguration(formValue);
      });

    // Écouter le statut de sauvegarde automatique
    this.autoSaveService.autoSaveStatus$
      .pipe(takeUntil(this.destroy$))
      .subscribe(status => {
        this.autoSaveStatus = status;
      });

    // Écouter l'état de l'historique
    this.historyManager.historyState$
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        this.historyState = state;
        this.recentHistory = this.historyManager.getHistoryEntries(5);
      });
  }

  /**
   * Initialisation du formulaire avec les données existantes
   */
  private async initializeForm(): Promise<void> {
    // Initialiser avec la configuration actuelle SANS déclencher de sauvegarde
    const currentConfig = this.configManager.currentConfiguration;
    if (currentConfig) {
      // Étape 1: Désactiver complètement l'auto-save pendant l'initialisation
      this.autoSaveService.suppressAutoSave();
      console.log('🔧 Initialisation: auto-save désactivé');
      
      // Étape 2: Charger les données et ajouter à l'historique (sans risque de sauvegarde)
      this.loadFormData(currentConfig);
      this.historyManager.addToHistory(currentConfig, 'Configuration initiale', 'initial');
      console.log('🔧 Initialisation: données chargées et historique ajouté');
      
      // Étape 3: Démarrer l'auto-save et attendre qu'il soit complètement initialisé
      await this.startAutoSave();
      console.log('🔧 Initialisation: auto-save démarré');
      
      // Étape 4: Attendre un cycle complet avant de réactiver
      await new Promise(resolve => setTimeout(resolve, 100));
      console.log('🔧 Initialisation: cycle d\'attente terminé');
      
      // Étape 5: Réactiver l'auto-save maintenant que tout est stable
      this.autoSaveService.resumeAutoSave();
      console.log('🔧 Initialisation: auto-save réactivé - initialisation terminée');
    } else {
      // Si pas de configuration, juste démarrer l'auto-save normalement
      await this.startAutoSave();
    }
  }

  /**
   * Charge les données d'une configuration dans le formulaire
   * @param config Configuration multilingue à charger
   */
  private loadFormData(config: any): void {
    const langConfig = config.configurations[this.currentLanguage];
    if (langConfig && langConfig.data) {
      // Désactiver temporairement l'écoute des changements pour éviter la boucle
      this.demoForm.patchValue(langConfig.data, { emitEvent: false });
    }
  }

  /**
   * Met à jour la configuration avec les valeurs du formulaire
   * @param formValue Valeurs du formulaire à sauvegarder
   */
  private updateConfiguration(formValue: any): void {
    this.configManager.updateConfiguration(this.currentLanguage, formValue);
  }

  /**
   * Change la langue actuelle du formulaire
   * @param language Code de la nouvelle langue
   */
  switchLanguage(language: string): void {
    if (this.availableLanguages.includes(language)) {
      this.currentLanguage = language;
      const currentConfig = this.configManager.currentConfiguration;
      if (currentConfig) {
        // Charger les données sans déclencher de sauvegarde (c'est juste un changement de vue)
        this.autoSaveService.withSuppressedAutoSave(() => {
          this.loadFormData(currentConfig);
        });
      }
    }
  }

  /**
   * Définit la langue principale pour la synchronisation
   * @param language Code de la langue principale
   */
  setPrimaryLanguage(language: string): void {
    this.configManager.setPrimaryLanguage(language);
  }

  /**
   * Ajoute une nouvelle langue via prompt utilisateur
   */
  addLanguage(): void {
    const newLang = prompt('Code de la nouvelle langue (ex: es, it, pt):');
    if (newLang && newLang.length === 2) {
    }
  }

  /**
   * Supprime une langue après confirmation
   * @param language Code de la langue à supprimer
   */
  removeLanguage(language: string): void {
    if (confirm(`Êtes-vous sûr de vouloir supprimer la langue "${language}" ?`)) {
      if (this.currentLanguage === language) {
        this.currentLanguage = this.primaryLanguage;
      }
    }
  }

  /**
   * Clone les données de la langue principale vers la langue actuelle
   */
  cloneFromPrimary(): void {
    // La synchronisation depuis la langue principale va déclencher un changement de configuration
    // On laisse cela déclencher une sauvegarde car c'est une action intentionnelle de l'utilisateur
    this.configManager.cloneConfiguration(this.primaryLanguage, this.currentLanguage);
  }

  /**
   * Démarre la sauvegarde automatique avec un délai d'1 seconde
   */
  async startAutoSave(): Promise<void> {
    await this.autoSaveService.startAutoSave(1000); // 1 seconde de délai
  }

  /**
   * Arrête la sauvegarde automatique
   */
  stopAutoSave(): void {
    this.autoSaveService.stopAutoSave();
  }

  /**
   * Bascule l'état de la sauvegarde automatique
   */
  toggleAutoSave(): void {
    if (this.autoSaveStatus.isActive) {
      this.stopAutoSave();
    } else {
      this.startAutoSave();
    }
  }

  /**
   * Effectue une sauvegarde manuelle
   * @returns Promise résolue après completion de la sauvegarde
   */
  async manualSave(): Promise<void> {
    try {
      await this.autoSaveService.manualSave();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde manuelle:', error);
    }
  }

  /**
   * Modifie le délai de debounce pour la sauvegarde automatique
   * @param milliseconds Nouveau délai en millisecondes
   */
  setDebounceDelay(milliseconds: number): void {
    this.autoSaveService.setDebounceDelay(milliseconds);
  }

  /**
   * Gestionnaire de changement du délai de debounce via interface utilisateur
   * @param event Événement de changement du select
   */
  onDebounceDelayChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    const milliseconds = parseInt(target.value, 10);
    this.setDebounceDelay(milliseconds);
  }

  /**
   * Fonction de tracking pour optimiser le rendu des listes d'historique
   * @param index Index dans la liste
   * @param entry Entrée d'historique
   * @returns Identifiant unique de l'entrée
   */
  trackByEntryId(index: number, entry: HistoryEntry): string {
    return entry.id;
  }

  /**
   * Effectue une opération d'annulation (undo)
   * La protection anti-spam est gérée par le HistoryManagerService
   */
  undo(): void {
    const previousConfig = this.historyManager.undo();
    if (previousConfig) {
      // setHistoryConfiguration active le flag isHistoryOperation pour éviter les boucles
      this.configManager.setHistoryConfiguration(previousConfig);
      this.loadFormData(previousConfig);
      // Sauvegarder sans ajouter à l'historique (c'est une restauration)
      this.autoSaveService.saveWithoutHistory().catch(error => {
        console.error('❌ Erreur sauvegarde après undo:', error);
      }).finally(() => {
        this.historyManager.clearFromHistory();
      });
    }
  }

  /**
   * Effectue une opération de rétablissement (redo)
   * La protection anti-spam est gérée par le HistoryManagerService
   */
  redo(): void {
    const nextConfig = this.historyManager.redo();
    if (nextConfig) {
      // setHistoryConfiguration active le flag isHistoryOperation pour éviter les boucles
      this.configManager.setHistoryConfiguration(nextConfig);
      this.loadFormData(nextConfig);
      // Sauvegarder sans ajouter à l'historique (c'est une restauration)
      this.autoSaveService.saveWithoutHistory().catch(error => {
        console.error('❌ Erreur sauvegarde après redo:', error);
      }).finally(() => {
        this.historyManager.clearFromHistory();
      });
    }
  }

  /**
   * Navigue directement vers une entrée spécifique de l'historique
   * @param entry Entrée d'historique vers laquelle naviguer
   */
  jumpToHistoryEntry(entry: HistoryEntry): void {
    if (confirm(`Revenir à la configuration "${entry.description}" ?`)) {
      console.log('📜 Navigation vers une entrée d\'historique');
      // Utiliser la méthode spéciale pour les opérations d'historique
      this.configManager.setHistoryConfiguration(entry.configuration);
      this.loadFormData(entry.configuration);
    }
  }

  /**
   * Efface tout l'historique après confirmation
   */
  clearHistory(): void {
    if (confirm('Êtes-vous sûr de vouloir effacer tout l\'historique ?')) {
      this.historyManager.clearHistory();
    }
  }

  /**
   * Ajoute une nouvelle étiquette au formulaire
   */
  addTag(): void {
    const newTag = prompt('Nouvelle étiquette:');
    if (newTag && newTag.trim()) {
      const currentTags = this.demoForm.get('tags')?.value || [];
      if (!currentTags.includes(newTag.trim())) {
        this.demoForm.patchValue({
          tags: [...currentTags, newTag.trim()]
        });
      }
    }
  }

  /**
   * Supprime une étiquette du formulaire
   * @param index Index de l'étiquette à supprimer
   */
  removeTag(index: number): void {
    const currentTags = this.demoForm.get('tags')?.value || [];
    currentTags.splice(index, 1);
    this.demoForm.patchValue({ tags: currentTags });
  }

  /**
   * Récupère le message d'erreur pour un champ spécifique
   * @param fieldPath Chemin du champ dans le formulaire
   * @returns Message d'erreur ou null si aucune erreur
   */
  getFieldError(fieldPath: string): string | null {
    const field = this.demoForm.get(fieldPath);
    if (field && field.invalid && field.touched) {
      if (field.errors?.['required']) return 'Ce champ est requis';
      if (field.errors?.['minlength']) return `Minimum ${field.errors['minlength'].requiredLength} caractères`;
      return 'Champ invalide';
    }
    return null;
  }

  /**
   * Formate un timestamp en texte relatif (il y a X minutes/secondes)
   * @param timestamp Timestamp à formater
   * @returns Texte formaté indiquant le temps écoulé
   */
  formatTimeAgo(timestamp: number): string {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor(diff / 1000);

    if (minutes > 0) return `Il y a ${minutes} minute${minutes > 1 ? 's' : ''}`;
    return `Il y a ${seconds} seconde${seconds > 1 ? 's' : ''}`;
  }

  /**
   * Récupère l'icône correspondant à l'état actuel de la sauvegarde
   * @returns Emoji représentant l'état de sauvegarde
   */
  getStatusIcon(): string {
    if (this.autoSaveStatus.saveInProgress) return '⏳';
    if (this.autoSaveStatus.saveError) return '❌';
    if (this.autoSaveStatus.lastSaveTime) return '✅';
    return '💾';
  }
}